import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import { RotateCcw, Trash } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import Timestamp from 'app/frontend/base/components/Timestamp'
import {
  awaitPendingMessage,
  updateNotification,
} from 'app/frontend/base/api/notification'
import { useTeam } from 'app/frontend/base/config/context/team'

type NotificationWarningModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  notifications: wowaudit.Notification[]
}

const NotificationWarningModal = ({
  open,
  onOpenChange,
  notifications,
}: NotificationWarningModalProps) => {
  const { team } = useTeam()
  const queryClient = useQueryClient()

  const [resendingId, setResendingId] = useState<number | null>(null)
  const [changeMethod, setChangeMethod] = useState<'pending' | 'abandoned'>(
    'pending',
  )

  const handleChange = async (
    notificationId: number,
    deliveryStatus: 'pending' | 'abandoned',
  ) => {
    setChangeMethod(deliveryStatus)
    setResendingId(notificationId)
    const response = await updateNotification(
      notificationId,
      team.id,
      deliveryStatus,
    )

    if (deliveryStatus === 'abandoned') {
      toast.success(
        response.success
          ? 'Notification abandoned'
          : 'Failed to abandon notification',
      )
      setResendingId(null)
      queryClient.invalidateQueries({
        queryKey: ['notifications', { id: team.id }],
      })
      return
    }

    if (response.success) {
      awaitPendingMessage(team.id, notificationId, (notification) => {
        const { deliveryStatus, errorStatus } = notification

        if (deliveryStatus === 'delivered') {
          toast.success('Notification sent successfully')
        } else {
          toast.error(
            `Failed to deliver notification. ${errorStatus} (${deliveryStatus})`,
          )
        }

        setResendingId(null)
        queryClient.invalidateQueries({
          queryKey: ['notifications', { id: team.id }],
        })
      })
    } else {
      toast.error('Failed to resend notification')
      setResendingId(null)
    }
  }

  const columns: ColumnDef<wowaudit.Notification>[] = [
    {
      header: 'Recipient',
      accessorKey: 'recipientName',
      cell: ({ row }) => (
        <span>
          {row.original.recipientName ?? (
            <span className={'italic opacity-70'}>Team</span>
          )}
        </span>
      ),
    },
    {
      header: 'Error',
      accessorKey: 'errorStatus',
      cell: ({ row }) => (
        <span>{row.original.errorStatus || 'Unknown error'}</span>
      ),
    },
    {
      header: 'Last attempt',
      accessorKey: 'lastAttemptedAt',
      cell: ({ row }) => (
        <Timestamp time={row.original.lastAttemptedAt} markOutdatedDays={0} />
      ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) => (
        <div className='flex gap-2 justify-end'>
          <Button
            size='xs'
            variant='outline'
            loading={
              resendingId === row.original.id && changeMethod === 'pending'
            }
            disabled={resendingId !== null}
            onClick={() => handleChange(row.original.id, 'pending')}
            className='w-24'
          >
            <RotateCcw className='w-3 h-3 mr-2' /> Resend
          </Button>

          <Button
            size='xs'
            variant='outline'
            loading={
              resendingId === row.original.id && changeMethod === 'abandoned'
            }
            disabled={resendingId !== null}
            onClick={() => handleChange(row.original.id, 'abandoned')}
            className='w-24'
          >
            <Trash className='w-3 h-3 mr-2' /> Abandon
          </Button>
        </div>
      ),
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Failed notifications</DialogTitle>
        </DialogHeader>
        {notifications.length === 0 ? (
          <div className='py-8 text-center text-muted-foreground'>
            No failed notifications.
          </div>
        ) : (
          <DataTable
            includeHeader={true}
            columns={columns}
            data={notifications}
            pagination={10}
            className='bg-muted'
          />
        )}
        <DialogFooter>
          <Button variant='secondary' onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NotificationWarningModal
